'use client';

import GalleryItem from '@/components/Gallery/item';
import { useIsMobileSsr } from '@/hooks/use-is-mobile-ssr';
import { cn } from '@/lib/utils';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Carousel, CarouselContent, CarouselItem } from '../ui/carousel';

const RelatedProducts = props => {
  const { hideProductName = false, showCustomizeOptions = false, classNames = {} } = props;
  const { products } = props;

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [carouselApi, setCarouselApi] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { isDesktop } = useIsMobileSsr();
  const slidesToScroll = isDesktop ? 1 : 2;

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const onSelect = () => {
      if (!carouselApi) {
        return;
      }

      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
      setSelectedIndex(carouselApi.selectedScrollSnap());
    };

    onSelect();
    carouselApi.on('reInit', onSelect);
    carouselApi.on('select', onSelect);

    return () => {
      carouselApi.off('reInit', onSelect);
      carouselApi.off('select', onSelect);
    };
  }, [carouselApi]);

  return (
    <>
      <Carousel
        className={cn('relative', classNames.carousel)}
        opts={{ dragFree: isDesktop, align: 'start', slidesToScroll }}
        setApi={setCarouselApi}
      >
        <CarouselContent className={classNames.carouselContent}>
          {products?.map(product => {
            return (
              <CarouselItem
                key={product.id}
                className={classNames.carouselItem}
              >
                <GalleryItem
                  key={product.id}
                  item={product}
                  hideProductName={hideProductName}
                  showCustomizeOptions={showCustomizeOptions}
                  showRating={false}
                  classes={{
                    name: 'text-[17px] leading-6',
                    price: 'text-[15px] leading-5',
                    imageLink: 'lg_mb-2.5',
                    priceWrapper: 'mt-1.5',
                  }}
                />
              </CarouselItem>
            );
          })}
        </CarouselContent>
        {canScrollPrev || canScrollNext ? (
          <>
            <CarouselArrow
              type='prev'
              className={classNames.carouselPrevArrow}
              onClick={() => {
                carouselApi?.scrollPrev();
              }}
              disabled={!canScrollPrev}
            />
            <CarouselArrow
              type='next'
              className={classNames.carouselNextArrow}
              onClick={() => {
                carouselApi?.scrollNext();
              }}
              disabled={!canScrollNext}
            />
          </>
        ) : null}
      </Carousel>

      {products?.length ? (
        <ul className='mt-2.5 flex items-center justify-center gap-x-1.5 lg_hidden'>
          {Array.from({ length: Math.ceil((products?.length || 0) / slidesToScroll) }).map((_, i) => {
            return (
              <li
                key={i}
                className={clsx(
                  selectedIndex === i ? 'w-[50px] rounded-[25px]' : 'w-[5px] rounded-[50%]',
                  'h-[5px] bg-E7DED5 ',
                )}
              ></li>
            );
          })}
        </ul>
      ) : null}
    </>
  );
};

const CarouselArrow = ({ type, className, onClick, disabled }) => {
  const isSlidePrev = type === 'prev';
  const isSlideNext = type === 'next';

  return (
    <button
      className={cn(
        'hidden h-10 w-8 cursor-pointer items-center justify-center bg-white/50 lg_flex',
        'absolute border-none outline-0',
        isSlideNext && 'right-0 rotate-180',
        isSlidePrev && 'left-0',
        'z-[2]',
        className,
      )}
      type='button'
      onClick={onClick}
      disabled={disabled}
    >
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='12'
        height='22'
        fill='none'
        viewBox='0 0 12 22'
      >
        <path
          stroke='#80583F'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          d='M10.91 1L1.68 11l9.23 10'
        />
        <path
          stroke='#000'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeOpacity='0.2'
          strokeWidth='2'
          d='M10.91 1L1.68 11l9.23 10'
        />
      </svg>
    </button>
  );
};

export default RelatedProducts;
